// Dashboard global filter bar: the filter registry, the FilterBar schema builder, and the
// per-widget request builder. Shared by the display page (renders the bar) and the editor
// drawer (stages the defaults) so both always agree on keys, labels, and value shapes.
import moment from 'moment'
import { formatUtmValue } from '../../../util/utmAttribution'

export const FILTER_KEYS = ['period', 'location', 'vertical', 'genre', 'event_name', 'event', 'staff', 'payment_method', 'purchase_type', 'promo', 'email_template', 'email_template_type', 'traffic'] as const
export type FilterKey = typeof FILTER_KEYS[number]

export const FILTER_LABELS: Record<FilterKey, string> = {
  period: 'Date',
  location: 'Location',
  vertical: 'Vertical',
  genre: 'Genre',
  event_name: 'Event by name',
  event: 'Event at location',
  staff: 'Staff member',
  payment_method: 'Payment method',
  purchase_type: 'Purchase type',
  promo: 'Promotion',
  email_template: 'Email template',
  email_template_type: 'Email template type',
  traffic: 'Traffic attribution',
}

export const FILTER_DESCRIPTIONS: Record<FilterKey, string> = {
  period: 'Applies one time period to every widget that is not set to all time.',
  location: 'Limits widgets to the selected groups. Widgets scoped to this group show no data when it is not selected.',
  vertical: 'Limits event data to events in the selected verticals.',
  genre: 'Limits event data to events in the selected genres.',
  event_name: 'Limits event data to events with these names, across every location.',
  event: 'Limits event data to specific events at specific locations.',
  staff: 'Limits event, checklist, and resource data to the selected staff members.',
  payment_method: 'Limits payment widgets to the selected methods.',
  purchase_type: 'Limits purchase widgets to bookings, gift cards, or merchandise.',
  promo: 'Limits booking, transaction, payment, and purchase data to rows that used the selected promotions.',
  email_template: 'Limits email widgets to emails sent from the selected templates. Simple emails are not included while this filter is active.',
  email_template_type: 'Limits email widgets to emails sent from templates of the selected types. Simple emails are not included while this filter is active.',
  traffic: 'Limits booking, transaction, gift card, payment, purchase and cart data to the selected traffic sources, mediums and campaigns (utm parameters).',
}

export const FILTER_ICONS: Record<FilterKey, string> = {
  period: 'icon-calendar-60',
  location: 'icon-pin',
  vertical: 'icon-components',
  genre: 'icon-tag',
  event_name: 'icon-app',
  event: 'icon-square-pin',
  staff: 'icon-single-02',
  payment_method: 'icon-credit-card',
  purchase_type: 'icon-cart',
  promo: 'icon-tag',
  email_template: 'icon-email-85',
  email_template_type: 'icon-bullet-list-67',
  traffic: 'icon-world',
}

const PURCHASE_TYPE_OPTIONS = [
  { label: 'Bookings', value: 'booking' },
  { label: 'Gift cards', value: 'gift_card' },
  { label: 'Merchandise', value: 'merchandise' },
]

// Mirrors FilterBar.vue's FilterDef (not exported there).
export interface FilterOption { label: string; value: string | number; group?: string; badge?: string }
interface FilterDef {
  key: string
  type: 'text' | 'select' | 'multiselect' | 'toggle' | 'daterange' | 'numberrange' | 'timerange' | 'selecttext'
  label: string
  placeholder?: string
  operatorLabel?: string
  options?: FilterOption[]
  primary?: boolean
  presets?: Array<{ key: string; label: string; values: Array<string | number>; divided?: boolean }>
  presetsLabel?: string
  // multiselect only: "All" row plus tri-state group header checkboxes in the picker (see FilterBar).
  selectAll?: boolean
  selectAllLabel?: string
  rangeOption?: string | number
  rangeKey?: string
  hidden?: boolean
}

export interface FilterOptions {
  games: Array<{ id: number; name: string; company_group_id: number; vertical_id: number | null; genre_id: number | null; group_name: string }>
  staff: Array<{ id: number; first_name: string; last_name: string; email: string }>
  groups: Array<{ id: number; name: string; currency: string; country?: string | null; state?: string | null; city?: string | null }>
  verticals: Array<{ id: number; name: string; active: number }>
  genres: Array<{ id: number; name: string; active: number; vertical_id: number }>
  timePeriodTypes: Array<{ id: number; name: string; period_key: string }>
  paymentMethods: Array<{ label: string; value: string }>
  promos?: Array<{ label: string; value: string; group?: string }>
  email_templates?: Array<{ id: number; name: string; company_group_id: number; group_name: string; archived: number; template_type_id: number | null }>
  email_template_types?: Array<{ id: number; name: string; company_group_id: number; group_name: string }>
  utm_values?: { sources: string[]; mediums: string[]; campaigns: string[] }
}

export interface FilterBarConfig { enabled: boolean; keys: FilterKey[]; defaults: Record<string, any> }

const CUSTOM_RANGE_ID = 16

// Automatic refresh: fixed choices, never faster than five minutes.
export const REFRESH_INTERVAL_OPTIONS = [
  { label: 'Every 5 minutes', value: 5 },
  { label: 'Every 10 minutes', value: 10 },
  { label: 'Every 15 minutes', value: 15 },
  { label: 'Every 30 minutes', value: 30 },
  { label: 'Every hour', value: 60 },
]
export const MIN_REFRESH_MINUTES = 5

// Snap a stored value onto the offered choices (legacy rows may hold any number).
export function snapRefreshInterval(v: any): number {
  const n = Number(v)
  if (!Number.isFinite(n) || n <= 0) return MIN_REFRESH_MINUTES
  const hit = REFRESH_INTERVAL_OPTIONS.find(o => o.value >= n)
  return hit ? hit.value : REFRESH_INTERVAL_OPTIONS[REFRESH_INTERVAL_OPTIONS.length - 1].value
}

export function emptyConfig(): FilterBarConfig {
  return { enabled: false, keys: [], defaults: {} }
}

const PURCHASE_TYPES = PURCHASE_TYPE_OPTIONS.map(o => o.value)

// Same rules as update-dashboard's normalizeFilterBarConfig; a stored config is trusted but a
// missing or malformed one degrades to "no bar".
export function normalizeConfig(raw: any): FilterBarConfig {
  if (!raw || typeof raw !== 'object') return emptyConfig()
  const keys = (Array.isArray(raw.keys) ? raw.keys : []).filter((k: any, i: number, a: any[]) => (FILTER_KEYS as readonly string[]).includes(k) && a.indexOf(k) === i) as FilterKey[]
  const src = raw.defaults && typeof raw.defaults === 'object' ? raw.defaults : {}
  const defaults: Record<string, any> = {}
  const ints = (v: any) => (Array.isArray(v) ? v.map(Number).filter(Number.isInteger) : [])
  const strs = (v: any) => (Array.isArray(v) ? v.filter((s: any) => typeof s === 'string') : [])
  for (const k of keys) {
    if (k === 'period') { const p = Number(src.period); defaults.period = Number.isInteger(p) && p > 0 ? p : '' }
    else if (k === 'location' || k === 'vertical' || k === 'genre' || k === 'event' || k === 'staff' || k === 'email_template' || k === 'email_template_type') defaults[k] = ints(src[k])
    else if (k === 'event_name' || k === 'payment_method' || k === 'promo') defaults[k] = strs(src[k])
    else if (k === 'purchase_type') defaults[k] = strs(src[k]).filter((t: string) => PURCHASE_TYPES.includes(t))
    else if (k === 'traffic') defaults[k] = trafficValues(strs(src[k]))
  }
  if (keys.includes('period')) {
    const d = src.custom_dates && typeof src.custom_dates === 'object' ? src.custom_dates : {}
    const iso = (s: any) => (typeof s === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(s) ? s : '')
    defaults.custom_dates = { mode: d.mode === 'days' ? 'days' : 'range', days: /^\d{1,4}$/.test(String(d.days || '')) ? String(d.days) : '', from: iso(d.from), to: iso(d.to) }
  }
  return { enabled: raw.enabled === true || raw.enabled === 1, keys, defaults }
}

// Mirrors EditDashboard.vue getMethodKey: built-in methods map to their stored payments.type
// code, custom methods are stored under their own name.
export function getMethodKey(name: string): string {
  if (!name) return ''
  const mapping: Record<string, string> = {
    'pos payment': 'pos',
    'card': 'card',
    'cash': 'cash',
    'gift card': 'gift_card',
    'customer credit': 'customer_credit',
  }
  const lowerName = name.toLowerCase()
  return mapping[lowerName] || name
}

function staffDisplayName(u: { first_name?: string; last_name?: string; email?: string }): string {
  if (u.first_name && u.last_name) return u.first_name + ' ' + u.last_name
  if (u.first_name) return u.first_name
  if (u.last_name) return u.last_name
  return u.email || ''
}

export function isActive(key: string, value: any): boolean {
  if (value === undefined || value === null) return false
  if (key === 'custom_dates') {
    if (typeof value !== 'object') return false
    return value.mode === 'days' ? !!value.days : !!(value.from && value.to)
  }
  if (Array.isArray(value)) return value.length > 0
  return value !== ''
}

// State / city presets, in that order, each with the group ids it covers. Countries are not listed
// here because the picker's country header rows select a whole country. The widget editor keeps its
// own Country entries.
function locationPresets(groups: FilterOptions['groups']): Array<{ key: string; label: string; values: number[]; divided?: boolean }> {
  const presets: Array<{ key: string; label: string; values: number[]; divided?: boolean }> = []
  const levels: Array<[('state' | 'city'), string]> = [['state', 'State'], ['city', 'City']]
  for (const [field, title] of levels) {
    const byValue = new Map<string, number[]>()
    for (const g of groups) {
      const v = String(g[field] || '').trim()
      if (!v) continue
      if (!byValue.has(v)) byValue.set(v, [])
      byValue.get(v)!.push(Number(g.id))
    }
    const entries = [...byValue.entries()].sort((a, b) => a[0].localeCompare(b[0]))
    entries.forEach(([value, ids], i) => presets.push({ key: field + ':' + value, label: `${title}: ${value} (${ids.length})`, values: ids, divided: i === 0 && presets.length > 0 }))
  }
  return presets
}

// Email template and template type pickers. Archived templates stay listed with a badge because
// their events still exist; a group header is added only when the company has more than one group.
// Multi-group lists are sorted by group first so each header renders once (the payload arrives
// ordered by name, which would interleave groups).
function byGroup<T extends { group_name: string }>(rows: T[], multiGroup: boolean): T[] {
  if (!multiGroup) return rows
  return rows.map((r, i) => ({ r, i })).sort((a, b) => String(a.r.group_name || '').localeCompare(String(b.r.group_name || '')) || a.i - b.i).map(x => x.r)
}
export function emailTemplateOptions(opts: FilterOptions): FilterOption[] {
  const multiGroup = (opts.groups || []).length > 1
  return byGroup(opts.email_templates || [], multiGroup).map(t => ({
    label: t.name,
    value: t.id,
    ...(Number(t.archived) === 1 ? { badge: 'Archived' } : {}),
    ...(multiGroup ? { group: t.group_name } : {}),
  }))
}
export function emailTemplateTypeOptions(opts: FilterOptions): FilterOption[] {
  const multiGroup = (opts.groups || []).length > 1
  return byGroup(opts.email_template_types || [], multiGroup).map(t => ({
    label: t.name,
    value: t.id,
    ...(multiGroup ? { group: t.group_name } : {}),
  }))
}

// Traffic attribution pickers: raw lowercase values from the dashboard payload, shown title-cased.
export function utmValueOptions(opts: FilterOptions, dim: 'sources' | 'mediums' | 'campaigns'): FilterOption[] {
  const values = opts.utm_values && Array.isArray(opts.utm_values[dim]) ? opts.utm_values[dim] : []
  return values.map(v => ({ label: formatUtmValue(v), value: v }))
}
// The bar carries all three dimensions in ONE filter: values are prefixed 'source:', 'medium:' or
// 'campaign:' and rendered under a group header each, like the promotion filter's groups and codes.
const TRAFFIC_DIMS: Array<{ prefix: 'source' | 'medium' | 'campaign'; dim: 'sources' | 'mediums' | 'campaigns'; group: string; wire: string }> = [
  { prefix: 'source', dim: 'sources', group: 'Sources', wire: 'utm_sources' },
  { prefix: 'medium', dim: 'mediums', group: 'Mediums', wire: 'utm_mediums' },
  { prefix: 'campaign', dim: 'campaigns', group: 'Campaigns', wire: 'utm_campaigns' },
]
export function trafficValues(raw: string[]): string[] {
  const out: string[] = []
  for (const v of raw) {
    const i = v.indexOf(':')
    if (i <= 0) continue
    const prefix = v.slice(0, i)
    const value = v.slice(i + 1).trim().toLowerCase()
    if (!TRAFFIC_DIMS.some(d => d.prefix === prefix) || !value) continue
    const norm = prefix + ':' + value
    if (!out.includes(norm)) out.push(norm)
  }
  return out
}
export function trafficOptions(opts: FilterOptions): FilterOption[] {
  const options: FilterOption[] = []
  for (const d of TRAFFIC_DIMS) {
    for (const o of utmValueOptions(opts, d.dim)) options.push({ label: o.label, value: d.prefix + ':' + o.value, group: d.group })
  }
  return options
}
// Splits the bar's prefixed selection into the three arrays the widget lambda reads.
export function trafficWire(raw: any): Record<string, string[]> {
  const out: Record<string, string[]> = {}
  for (const v of trafficValues(Array.isArray(raw) ? raw.filter((x: any) => typeof x === 'string') : [])) {
    const d = TRAFFIC_DIMS.find(x => v.startsWith(x.prefix + ':'))
    if (!d) continue
    if (!out[d.wire]) out[d.wire] = []
    out[d.wire].push(v.slice(d.prefix.length + 1))
  }
  return out
}

export function buildFilterSchema(keys: FilterKey[], values: Record<string, any>, opts: FilterOptions): FilterDef[] {
  const defs: FilterDef[] = []
  for (const key of keys) {
    if (key === 'period') {
      // The custom range dates are edited inside the Date popover (revealed by the Custom
      // range option); the daterange def carries the value but renders no chip of its own.
      defs.push({ key: 'period', type: 'select', label: FILTER_LABELS.period, primary: true, rangeOption: CUSTOM_RANGE_ID, rangeKey: 'custom_dates', options: (opts.timePeriodTypes || []).map(t => ({ label: t.name, value: t.id })) })
      defs.push({ key: 'custom_dates', type: 'daterange', label: 'Custom dates', primary: true, hidden: true })
    } else if (key === 'location') {
      // Grouped under a country heading in the picker (FilterBar renders `group` as a header row),
      // ordered country, state, name; the state stays in the label when a country has several.
      const country = (g: FilterOptions['groups'][number]) => String(g.country || '').trim() || 'No country set'
      const state = (g: FilterOptions['groups'][number]) => String(g.state || '').trim()
      const sorted = [...(opts.groups || [])].sort((a, b) =>
        country(a).localeCompare(country(b)) || state(a).localeCompare(state(b)) || String(a.name || '').localeCompare(String(b.name || '')))
      defs.push({ key, type: 'multiselect', primary: true, label: FILTER_LABELS.location, options: sorted.map(g => ({ label: `${g.currency} · ${g.name}` + (state(g) ? ` (${state(g)})` : ''), value: g.id, group: country(g) })), presets: locationPresets(opts.groups || []), presetsLabel: 'Select by location', selectAll: true, selectAllLabel: 'All locations' })
    } else if (key === 'vertical') {
      const selected = new Set((values.vertical || []).map(Number))
      defs.push({ key, type: 'multiselect', primary: true, label: FILTER_LABELS.vertical, options: (opts.verticals || []).filter(v => v.active || selected.has(Number(v.id))).map(v => ({ label: v.name, value: v.id })) })
    } else if (key === 'genre') {
      const selectedVerticals = new Set((values.vertical || []).map(Number))
      const selectedGenres = new Set((values.genre || []).map(Number))
      const seen = new Set<number>()
      const options: FilterOption[] = []
      for (const g of opts.genres || []) {
        if (seen.has(Number(g.id))) continue
        if (selectedVerticals.size > 0 && !selectedVerticals.has(Number(g.vertical_id)) && !selectedGenres.has(Number(g.id))) continue
        if (!g.active && !selectedGenres.has(Number(g.id))) continue
        seen.add(Number(g.id))
        options.push({ label: g.name, value: g.id })
      }
      defs.push({ key, type: 'multiselect', primary: true, label: FILTER_LABELS.genre, options })
    } else if (key === 'event_name') {
      const names = Array.from(new Set((opts.games || []).map(g => g.name))).sort((a, b) => a.localeCompare(b))
      defs.push({ key, type: 'multiselect', primary: true, label: FILTER_LABELS.event_name, options: names.map(n => ({ label: n, value: n })) })
    } else if (key === 'event') {
      defs.push({ key, type: 'multiselect', primary: true, label: FILTER_LABELS.event, options: (opts.games || []).map(g => ({ label: `${g.name} (${g.group_name})`, value: g.id })) })
    } else if (key === 'staff') {
      defs.push({ key, type: 'multiselect', primary: true, label: FILTER_LABELS.staff, options: (opts.staff || []).map(u => ({ label: staffDisplayName(u), value: u.id })) })
    } else if (key === 'payment_method') {
      defs.push({ key, type: 'multiselect', primary: true, label: FILTER_LABELS.payment_method, options: opts.paymentMethods || [] })
    } else if (key === 'purchase_type') {
      defs.push({ key, type: 'multiselect', primary: true, label: FILTER_LABELS.purchase_type, options: PURCHASE_TYPE_OPTIONS })
    } else if (key === 'promo') {
      defs.push({ key, type: 'multiselect', primary: true, label: FILTER_LABELS.promo, options: opts.promos || [] })
    } else if (key === 'email_template') {
      defs.push({ key, type: 'multiselect', primary: true, label: FILTER_LABELS.email_template, options: emailTemplateOptions(opts) })
    } else if (key === 'email_template_type') {
      defs.push({ key, type: 'multiselect', primary: true, label: FILTER_LABELS.email_template_type, options: emailTemplateTypeOptions(opts) })
    } else if (key === 'traffic') {
      defs.push({ key, type: 'multiselect', primary: true, label: FILTER_LABELS.traffic, options: trafficOptions(opts) })
    }
  }
  return defs
}

// One label per active filter, in config order: "Date: Last month", "Location: 2 selected".
export function activeLabelParts(values: Record<string, any>, keys: FilterKey[], schema: FilterDef[]): Array<{ key: FilterKey; label: string }> {
  const defByKey = new Map(schema.map(d => [d.key, d]))
  const parts: Array<{ key: FilterKey; label: string }> = []
  for (const key of keys) {
    const v = values[key]
    if (!isActive(key, v)) continue
    const def = defByKey.get(key)
    let text = ''
    if (key === 'period') {
      const opt = def && def.options ? def.options.find(o => Number(o.value) === Number(v)) : null
      text = opt ? String(opt.label) : ''
      if (Number(v) === CUSTOM_RANGE_ID) {
        const cd = values.custom_dates
        if (isActive('custom_dates', cd)) {
          text = cd.mode === 'days' ? 'Last ' + cd.days + ' day' + (Number(cd.days) === 1 ? '' : 's') : cd.from + ' to ' + cd.to
        } else {
          continue   // custom range without dates is not an active date filter
        }
      }
    } else if (Array.isArray(v)) {
      if (v.length > 2) text = v.length + ' selected'
      else text = v.map(val => { const o = def && def.options ? def.options.find(x => String(x.value) === String(val)) : null; return o ? String(o.label) : String(val) }).join(', ')
    } else {
      text = String(v)
    }
    parts.push({ key, label: FILTER_LABELS[key] + ': ' + text })
  }
  return parts
}

// Date override active only when a period is chosen and, for the custom range, dates are set.
export function globalPeriodActive(values: Record<string, any>): boolean {
  if (!isActive('period', values.period)) return false
  if (Number(values.period) === CUSTOM_RANGE_ID) return isActive('custom_dates', values.custom_dates)
  return true
}

interface WidgetQuery { dateParams: string; filtersParam: string }

export function buildWidgetQuery(values: Record<string, any>, item: { is_all_time: any }, timePeriodTypes: Array<{ id: number; period_key: string }>): WidgetQuery {
  let dateParams = ''
  if (globalPeriodActive(values) && !item.is_all_time) {
    if (Number(values.period) === CUSTOM_RANGE_ID) {
      const cd = values.custom_dates
      let from: string
      let to: string
      if (cd.mode === 'days') {
        const days = Math.max(1, parseInt(cd.days, 10) || 1)
        to = moment().format('YYYY-MM-DD')
        from = moment().subtract(days - 1, 'days').format('YYYY-MM-DD')
      } else {
        from = cd.from
        to = cd.to
      }
      dateParams = '&time_period_type=custom_range&time_period_type_id=' + CUSTOM_RANGE_ID + '&start_date=' + from + '&end_date=' + to + '&global_date=1'
    } else {
      const tpt = (timePeriodTypes || []).find(t => Number(t.id) === Number(values.period))
      if (tpt) {
        const today = moment().format('YYYY-MM-DD')
        dateParams = '&time_period_type=' + tpt.period_key + '&time_period_type_id=' + tpt.id + '&start_date=' + today + '&end_date=' + today + '&global_date=1'
      }
    }
  }
  const f: Record<string, any> = {}
  const add = (name: string, v: any) => { if (Array.isArray(v) && v.length > 0) f[name] = v }
  add('group_ids', values.location)
  add('vertical_ids', values.vertical)
  add('genre_ids', values.genre)
  add('game_names', values.event_name)
  add('game_ids', values.event)
  add('company_user_ids', values.staff)
  add('payment_types', values.payment_method)
  add('purchase_types', values.purchase_type)
  add('promos', values.promo)
  add('email_template_ids', values.email_template)
  add('email_template_type_ids', values.email_template_type)
  for (const [name, arr] of Object.entries(trafficWire(values.traffic))) add(name, arr)
  const filtersParam = Object.keys(f).length > 0 ? '&filters=' + encodeURIComponent(JSON.stringify(f)) : ''
  return { dateParams, filtersParam }
}
